<script setup>
import { computed } from "vue";
import BrandButton from "@/Components/Brand/BrandButton.vue";

const props = defineProps({
    headline: {
        type: String,
        required: true,
    },
    body: {
        type: String,
        default: null,
    },
    ctaLabel: {
        type: String,
        required: true,
    },
    ctaHref: {
        type: String,
        required: true,
    },
    // Public image path, resolved through the asset() mixin (Vapor-safe).
    image: {
        type: String,
        required: true,
    },
    variant: {
        type: String,
        default: "navy",
        validator: (value) => ["navy", "purple", "gold", "red"].includes(value),
    },
    // Compact banners (guest promo pair) use smaller type and padding.
    compact: {
        type: Boolean,
        default: false,
    },
    // Full replacement for the image's layout classes (e.g. the collage gets
    // a diagonal clip so the headline never sits over it).
    imageClass: {
        type: String,
        default: null,
    },
    // Mobile-specific artwork (full-width at the card bottom); the main
    // image then renders lg-only.
    mobileImage: {
        type: String,
        default: null,
    },
});

const backgroundClass = computed(
    () =>
        ({
            navy: "bg-brand-navy",
            purple: "bg-brand-purple",
            gold: "bg-[#f5a623]",
            red: "bg-brand-red",
        })[props.variant],
);

// The white CTA's hover wipe is red by default, which vanishes against a
// red banner, so red banners wipe navy instead (BH-58).
const buttonVariant = computed(() =>
    props.variant === "red" ? "whiteOnRed" : "white",
);
</script>

<template>
    <aside
        class="relative flex flex-col overflow-hidden rounded-brand text-white lg:block"
        :class="backgroundClass"
    >
        <div
            class="relative z-10 flex flex-col items-start"
            :class="
                compact
                    ? 'gap-4 px-8 py-8 lg:max-w-[60%]'
                    : 'gap-5 px-8 py-9 sm:px-14 sm:py-12 lg:max-w-[52%] xl:max-w-[560px]'
            "
        >
            <h2
                class="whitespace-pre-line font-brand-display font-black italic uppercase leading-[0.95]"
                :class="
                    compact
                        ? 'text-[32px] sm:text-[38px]'
                        : 'text-[40px] sm:text-[61px]'
                "
            >
                {{ headline }}
            </h2>
            <p
                v-if="body"
                class="max-w-[467px] font-brand text-[15px] leading-[1.5] text-white/90"
            >
                {{ body }}
            </p>
            <BrandButton
                :href="ctaHref"
                :variant="buttonVariant"
                :class="
                    compact
                        ? 'px-6 py-3 text-[13px] uppercase tracking-wide'
                        : 'px-7 py-3.5 text-[15px]'
                "
            >
                {{ ctaLabel }}
            </BrandButton>
        </div>

        <!-- Mobile gets the frame's diagonal collage full-width at the card
             bottom (GH-1090); desktop keeps the right-anchored artwork. At lg
             the artwork keeps its full height but is anchored to the copy's
             right edge and slid left by its transparent third, so the
             products start where the copy ends and the excess clips off the
             right. -->
        <img
            v-if="mobileImage"
            :src="asset(mobileImage)"
            alt=""
            class="pointer-events-none mt-2 w-full select-none lg:hidden"
        />
        <img
            :src="asset(image)"
            alt=""
            class="pointer-events-none select-none"
            :class="[
                imageClass ?? [
                    'max-lg:-mt-2 max-lg:w-full lg:absolute lg:inset-y-0 lg:h-full lg:w-auto lg:max-w-none lg:-translate-x-[30%] xl:left-auto xl:right-0 xl:translate-x-0',
                    compact ? 'lg:left-[60%]' : 'lg:left-[55%]',
                ],
                mobileImage ? 'max-lg:hidden' : '',
            ]"
        />
    </aside>
</template>
